import { generatePath } from 'react-router-dom'

import {
    ProductCatalogTabId,
    RETAIL_PERFORMANCE_TAB,
} from 'const/productCatalog'
import {
    AD_GROUP,
    AUTOMATION_TASK,
    CAMPAIGN,
    KEYWORD,
    NON_KEYWORD_TARGET,
    PRODUCT_AD,
    PRODUCT_METADATA,
    SOV_KEYWORD,
} from 'const/resourceTypes'
import { ASIN_DOMAINS, HELP_CENTER } from 'routing/externalUrls'
import { getPath } from 'routing/pagePaths'
import { LabelPageTabId } from 'routing/pageTabIds'
import { Alert, Campaign, CountryCode, OrganizationGroup } from 'types'

import { PageId } from './pages'

// Internal URLs
export const keywordUrl = (keywordId: string): string =>
    generatePath(getPath('keywordPage'), {
        keywordId,
    })

export const targetingUrl = (
    filters: any | undefined,
    tab:
        | 'keywords'
        | 'keywordSearchTerms'
        | 'productTargets'
        | 'productTargetSearchTerms'
): string => {
    const pageId: PageId = 'targetingSummaryPage'
    if (!filters) {
        return `${getPath(pageId)}/?tab=${tab}`
    }
    return `/?redirect=${pageId}&tab=${tab}&filters=${encodeURIComponent(
        JSON.stringify(filters)
    )}`
}

export const distributionAnalysisUrl = (
    filters: any | undefined,
    tab: 'sellerList' | 'overview'
): string => {
    const pageId: PageId = 'distributionAnalysisPage'
    if (!filters) {
        return `${getPath(pageId)}/?tab=${tab}`
    }
    return `/?redirect=${pageId}&tab=${tab}&filters=${encodeURIComponent(
        JSON.stringify(filters)
    )}`
}

export type ProductCatalogUrlTab =
    | 'all_products'
    | 'seller_central'
    | 'vendor_central'

export const productCatalogUrl = (
    filters: any | undefined = undefined,
    tab: ProductCatalogUrlTab = 'all_products'
): string => {
    const pageId: PageId = 'productCatalogPage'
    if (!filters) {
        return `${getPath(pageId)}/?tab=${tab}`
    }
    return `/?redirect=${pageId}&tab=${tab}&filters=${encodeURIComponent(
        JSON.stringify(filters)
    )}`
}

export const automationProfilesUrl = (): string => {
    return `${getPath('automationPage')}/?tab=profiles`
}

export const automationProfileUrl = (profileId: string): string =>
    generatePath(getPath('automationProfilePage'), {
        profileId,
    })

export const sovKeywordUrl = (sovKeywordId: string): string =>
    generatePath(getPath('sovKeywordPage'), {
        sovKeywordId,
    })

export const sovKeywordSummaryUrl = (
    filters: any | undefined,
    tab: 'timeline' | 'keywordPerformance' | 'manageKeywords'
): string => {
    const pageId: PageId = 'sovKeywordsSummaryPage'
    if (!filters) {
        return `${getPath(pageId)}/?tab=${tab}`
    }
    return `/?redirect=${pageId}&tab=${tab}&filters=${encodeURIComponent(
        JSON.stringify(filters)
    )}`
}

export const sovKeywordResearchUrl = (
    country: CountryCode,
    keyword: string
): string => {
    const path = generatePath(getPath('keywordResearchPage'))
    return `${path}?country=${country}&keyword=${keyword}`
}

export const sovKeywordResearchResultUrl = (
    country: CountryCode,
    keyword: string,
    asins?: string[]
): string => {
    const asinsString = asins ? asins.join(',') : ''
    const path = generatePath(getPath('keywordResearchPage'))
    return `${path}?asins=${asinsString}&country=${country}&keyword=${keyword}`
}

export const sovAsinReverseResearchUrl = (
    country: CountryCode,
    asin: string
): string => {
    const path = generatePath(getPath('keywordResearchPage'))
    return `${path}?country=${country}&asins=${asin}`
}

export const categoryPerformanceSummaryUrl = (): string => {
    return generatePath(getPath('categoryPerformanceSummaryPage'))
}

export const categoryPerformanceDetailUrl = (
    categoryId: string,
    params?: {
        dateRange?: string | string[]
        countryCode?: string
        tab?: string
    }
): string => {
    const queryableParams: Record<string, string> = {}
    if (Array.isArray(params?.dateRange)) {
        queryableParams.dateRange = params.dateRange.join(',')
    } else if (params?.dateRange) {
        queryableParams.dateRange = params.dateRange
    }
    if (params?.countryCode) {
        queryableParams.country = params.countryCode
    }
    if (params?.tab) {
        queryableParams.tab = params.tab
    }

    const queryParams = new URLSearchParams(queryableParams).toString()
    const path = generatePath(getPath('categoryDetailsPage'), {
        categoryId,
    })
    return `${path}${queryParams ? `?${queryParams}` : ''}`
}

export const campaignUrl = (campaignId: string, tabId?: string): string => {
    const path = generatePath(getPath('campaignPage'), {
        campaignId,
    })

    return tabId ? `${path}?tab=${tabId}` : path
}

export const segmentUrl = (segmentId: string, tabId?: string): string => {
    const path = generatePath(getPath('segmentPage'), {
        segmentId,
    })

    return tabId ? `${path}?tab=${tabId}` : path
}

export const adAccountUrl = (adAccountId: string): string =>
    generatePath(getPath('adAccountPage'), {
        adAccountId,
    })

export const productUrl = (asin: string, countryCode: string): string =>
    // Ideally the countryCode argument should be of type CountryCode
    generatePath(getPath('productPage'), {
        asin,
        countryCode,
    })

export const productDetailUrl = (
    asin: string,
    countryCode: string,
    tabId: ProductCatalogTabId = RETAIL_PERFORMANCE_TAB
): string => {
    const path = generatePath(getPath('productDetailPage'), {
        asin,
        countryCode,
    })
    return tabId ? `${path}?tab=${tabId}` : path
}

export const productAdUrl = (
    campaignId: string,
    productAdId?: string
): string =>
    generatePath(getPath('campaignProductAdPage'), {
        campaignId,
        productAdId,
    })

export const dashboardUrl = (
    dashboardId: string,
    options?: { tabId?: string }
): string => {
    const path = generatePath(getPath('dashboardPage'), { dashboardId })

    let searchParams = new URLSearchParams()
    if (options?.tabId) {
        searchParams.set('tab', options.tabId)
    }

    return `${path}?${searchParams.toString()}`
}

export const dashboardsUrl = (tabId?: string): string => {
    const path = generatePath(getPath('dashboardsSummaryPage'))

    return tabId ? `${path}?tab=${tabId}` : path
}

export const labelUrl = (labelId: string, tabId?: LabelPageTabId): string => {
    const path = generatePath(getPath('labelPage'), {
        labelId,
    })
    return tabId ? `${path}?tab=${tabId}` : path
}

export const changelogResourceUrl = (
    resourceType: string,
    resourceId: string,
    campaign: Campaign
): string => {
    if (resourceType === KEYWORD) {
        return keywordUrl(resourceId)
    }

    if (resourceType === CAMPAIGN) {
        return campaignUrl(resourceId)
    }

    if (resourceType === PRODUCT_AD) {
        return productAdUrl(campaign.id, resourceId)
    }

    if (resourceType === AUTOMATION_TASK) {
        return automationProfileUrl(resourceId)
    }

    throw new Error(
        `Attempted to build URL for unhandled resource type of ${resourceType}`
    )
}

export const adGroupUrl = (campaignId: string, adGroupId: string): string =>
    generatePath(getPath('adGroupPage'), {
        campaignId,
        adGroupId,
    })

export const portfolioUrl = (portfolioId: string): string =>
    generatePath(getPath('portfolioPage'), {
        portfolioId,
    })

export const organizationGroupUrl = (group: OrganizationGroup): string =>
    generatePath(getPath('organizationGroupPage'), {
        organizationGroupId: group.id,
    })

// External Amazon URLs
export const asinUrl = (
    asin: string,
    countryCode: CountryCode = 'US'
): string => {
    const domain = ASIN_DOMAINS[countryCode]
    return `https://${domain}/gp/product/${asin}`
}

export const sellerUrl = (
    sellerId: string,
    countryCode: CountryCode = 'US'
): string => {
    const domain = ASIN_DOMAINS[countryCode]
    return `https://${domain}/sp/?seller=${sellerId}`
}

// External Learning Hub URL
export const videoLearningPathsExternalUrl = (): string => {
    const { href } = new URL('https://fast.wistia.com/embed/channel/a2tgtglkx9')
    return href
}

// External help center URLs
export const helpCenterUrlSSO = (article?: string): string => {
    const path = generatePath(getPath('zendeskSSOPage'))
    return `${path}?return_to=${encodeURIComponent(`${HELP_CENTER}/hc/en-us/${article ?? ''}`)}`
}

export const contactUsExternalUrl = (authenticate?: boolean): string => {
    if (authenticate) {
        return helpCenterUrlSSO('requests/new')
    }
    return `${HELP_CENTER}/hc/en-us/requests/new`
}

export const automationCategoryExternalUrl = (): string =>
    helpCenterUrlSSO('sections/14713460879383-Advertising-Automations')

export const daypartingCategoryExternalUrl = (): string =>
    helpCenterUrlSSO('articles/14132123684119-Dayparting-Automation')

export const roiOptimizationCategoryExternalUrl = (): string =>
    helpCenterUrlSSO('articles/14131958979735-ROI-Optimization')

export const budgetMaximizationCategoryExternalUrl = (): string =>
    helpCenterUrlSSO(
        'articles/14132163992087-Budget-Maximization-Feature-Overview'
    )

export const keywordHarvestingCategoryExternalUrl = (): string =>
    helpCenterUrlSSO(
        'articles/14080520365335-Keyword-Harvesting-Feature-Overview'
    )

export const asinHarvestingCategoryExternalUrl = (): string =>
    helpCenterUrlSSO('articles/14185843527575-ASIN-Harvesting-Feature-Overview')

export const advancedBudgetControlCategoryExternalUrl = (): string =>
    helpCenterUrlSSO(
        'articles/14132303770519-Advanced-Budget-Control-Best-Practices'
    )

export const creatingVCIntegrationExternalUrl = (): string =>
    helpCenterUrlSSO(
        'articles/14378447057943-How-to-Add-Amazon-Selling-Partner-Integrations'
    )

export const alertUrl = (alert: Alert): string =>
    generatePath(getPath('alertPage'), {
        alertId: alert.id,
    })

export const alertTargetUrl = (
    targetType: string,
    targetId: string,
    campaign_id: string
): string => {
    // Ideally the targetType argument should be of type TriggeredAlertTarget['target_type']
    // Some of the switch cases do not apply to that type, so it probably needs to be updated
    switch (targetType) {
        case CAMPAIGN:
            return campaignUrl(targetId)
        case KEYWORD:
            return keywordUrl(targetId)
        case PRODUCT_AD:
            return productAdUrl(targetId)
        case SOV_KEYWORD:
            return sovKeywordUrl(targetId)
        case AD_GROUP:
            return adGroupUrl(campaign_id, targetId)
        case PRODUCT_METADATA: {
            const parts = targetId.split('_')
            return productUrl(parts[1], parts[0])
        }
        case NON_KEYWORD_TARGET:
            return `${campaignUrl(campaign_id)}?tab=targets`
        default:
            return campaignUrl(targetId)
    }
}

export const uploadPageUrl = generatePath(getPath('bulkUpdatesPage'))

export const advertiserUrl = (advertiserId: string): string =>
    generatePath(getPath('dspAdvertiserPage'), {
        advertiserId,
    })

export const orderUrl = (orderId: string): string =>
    generatePath(getPath('dspOrderPage'), {
        orderId,
    })

export const lineItemUrl = (lineItemId: string): string =>
    generatePath(getPath('dspLineItemPage'), {
        lineItemId,
    })

export const keywordSearchUrl = (
    keyword: string,
    countryCode: CountryCode = 'US'
): string => {
    const encodedUrlParam = encodeURIComponent(keyword.replaceAll(' ', '+'))
    return `https://${ASIN_DOMAINS[countryCode]}/s?k=${encodedUrlParam}`
}
